import React from 'react'
import { useRouter } from 'next/router'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { apiPaths } from 'api/kinerja/penilik-pegawai/config'
import { useAPISubmitPerencanaan } from 'api/kinerja/penilik-pegawai/perencanaan'
import { Card, CardBody } from 'app/KinerjaShared/components/Card'
import { Header } from 'app/KinerjaShared/components/Header'
import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { FooterAction } from 'app/KinerjaPenilikPegawaiPage/components/FooterAction'
import { PENGEMBANGAN_KOMPETENSI_HINTS } from 'app/KinerjaPenilikPegawaiPage/constants/pengembanganKompetensi'
import { DialogPengembanganKompetensiConfirmation } from './DialogPengembanganKompetensiConfirmation'
import { DialogPengembanganKompetensiInfo } from './DialogPengembanganKompetensiInfo'
import { DialogExitPrevention } from 'app/KinerjaPenilikPegawaiPage/components/DialogExitPrevention'
import ScrollDown from 'app/KinerjaPSPegawaiPage/components/ScrollDown'
import { usePengembanganKompetensiDraftStore } from 'app/KinerjaPenilikPegawaiPage/stores/kinerja-penilik-pegawai'
import { usePerencanaanPenilik } from 'app/KinerjaPenilikPegawaiPage/hooks/usePerencanaan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import useWarn from 'app/KinerjaShared/hooks/useWarn'
import { useStoreByUserId } from 'app/KinerjaPenilikPegawaiPage/hooks/useStoreByUserId'
import { IndicatorsForm } from './Form/IndicatorsForm'
import { ActivitiesForm } from './Form/ActivitiesForm'
import { usePenilikTrackEvent } from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikTrackEvent'
import usePrevious from 'utils/hooks/usePrevious'

import type {
  PengembanganKompetensiForm,
  RequestPerencanaanPengembanganKompetensi,
  PerencanaanPengembanganKompetensiActivity,
  PengembanganKompetensiLokasi,
} from 'types/kinerja-penilik-pegawai'

type PengembanganKompetensiProps = {}
const PengembanganKompetensi: React.FC<PengembanganKompetensiProps> = ({}) => {
  const router = useRouter()
  const toast = useToast()
  const { period } = usePeriodContext()
  const prevPeriodId = usePrevious(period.id)
  const { trackEvent } = usePenilikTrackEvent()

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState<boolean>(false)
  const [isExitPreventionDialogOpen, setExitPreventionDialogOpen] =
    React.useState<boolean>(false)
  const [isInfoDialogOpen, setInfoDialogOpen] = React.useState<boolean>(false)

  const formMethods = useForm<PengembanganKompetensiForm>({
    mode: 'onChange',
    defaultValues: {
      indicator: undefined,
      activities: [
        {
          kategori: undefined,
          kegiatan: undefined,
          lokasi: undefined,
          poin: 0,
        },
      ],
    },
  })
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
    watch,
  } = formMethods
  const indicatorValue = watch('indicator')
  const activitiesValue = watch('activities')

  // Summary
  const { perencanaan, isPengembanganKompetensiEditable, apiStatus } =
    usePerencanaanPenilik()

  // set Store by User ID
  useStoreByUserId()

  // API Submit Pengembangan Kompetensi form
  const { isLoading: isSubmitting, mutateAsync } =
    useAPISubmitPerencanaan<RequestPerencanaanPengembanganKompetensi>(
      apiPaths.pengembanganKompetensi
    )

  const { reset: resetPreventRouteChange } = useWarn({
    enabled: isDirty,
    bindPopState: true,
    onWarn: () => {
      setExitPreventionDialogOpen(true)
    },
  })

  const isNextDisabled = React.useMemo(
    () => !isValid || isSubmitting,
    [isSubmitting, isValid]
  )

  const { draftIndicator, draftActivities, hasDraft, setDraft } =
    usePengembanganKompetensiDraftStore()

  const resetForm = () => {
    setDraft(undefined, [])
    setConfirmationDialogOpen(false)
    resetPreventRouteChange()
  }

  const submit = handleSubmit(async (formData) => {
    const { indicator, activities } = formData

    try {
      await mutateAsync({
        periodeId: period.id,
        pengembanganKompetensiIndikatorId: indicator,
        pengembanganKompetensiKegiatanValues: (activities ?? []).map(
          ({ kategori, kegiatan, lokasi }) => ({
            kategoriId: kategori ?? '',
            kegiatanId: kegiatan ?? '',
            tempatMeningkatkanIndikator: lokasi ?? '',
          })
        ) as PerencanaanPengembanganKompetensiActivity[],
      })

      // Track Submission
      trackEvent('perencanaan pegawai', 'save draft perencanaan', {
        aspect: 'pengembangan kompetensi',
      })

      // Reset states
      resetForm()

      // Display toast
      toast({
        message: 'Berhasil simpan Pengembangan Kompetensi!',
        type: 'success',
      })

      // Redirect to Beranda
      setTimeout(() => {
        router.push(
          `${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/beranda`
        )
      }, 500)
    } catch (e) {
      setConfirmationDialogOpen(false)
      toast({
        message: 'Gagal simpan Pengembangan Kompetensi. Coba lagi.',
      })
    }
  })

  const handleBackToPegawaiPSHubPage = () => {
    const isNewActivity = window.history.length <= 2
    if (isNewActivity) {
      router.push(
        `${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/beranda`
      )
    } else {
      router.back()
    }
  }

  const handleSaveDraft = () => {
    setDraft(indicatorValue, activitiesValue)

    // Track save draft
    trackEvent('perencanaan pegawai', 'save draft perencanaan', {
      aspect: 'pengembangan kompetensi',
    })

    // Reset exit prevention dialog
    resetPreventRouteChange()
    setExitPreventionDialogOpen(false)
    handleBackToPegawaiPSHubPage()
  }

  React.useEffect(() => {
    if (Boolean(draftIndicator)) {
      setValue('indicator', draftIndicator)
    } else if (
      Boolean(perencanaan?.pengembanganKompetensiIndikator?.indikatorId) &&
      perencanaan?.pengembanganKompetensiIndikator?.indikatorId !== '0'
    ) {
      setValue(
        'indicator',
        perencanaan?.pengembanganKompetensiIndikator?.indikatorId
      )
    }

    if ((draftActivities ?? []).length > 0) {
      setValue('activities', draftActivities)
    } else if (
      (perencanaan?.pengembanganKompetensiKegiatanValues ?? []).length > 0
    ) {
      setValue(
        'activities',
        (perencanaan?.pengembanganKompetensiKegiatanValues ?? []).map(
          ({ kategori, kegiatan, tempatMeningkatkanIndikator }) => ({
            kategori: kategori.id,
            kegiatan: kegiatan.id,
            lokasi: tempatMeningkatkanIndikator as PengembanganKompetensiLokasi,
          })
        ) as PengembanganKompetensiForm['activities']
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasDraft,
    perencanaan?.pengembanganKompetensiIndikator,
    perencanaan?.pengembanganKompetensiKegiatanValues,
    draftIndicator,
    draftActivities,
  ])

  React.useEffect(() => {
    if (!!prevPeriodId && prevPeriodId !== period.id) {
      resetPreventRouteChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.id, prevPeriodId])

  return (
    <div className="pb-40">
      <Header title="Pengembangan Kompetensi" className="mb-9 print:hidden" />

      {apiStatus === 'loading' && (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}

      {apiStatus === 'success' && (
        <>
          <FormProvider {...formMethods}>
            <div className="grid grid-cols-10 gap-x-6">
              <div className="col-span-3">
                <Card className="sticky bg-white border rounded-md border-subdued top-24">
                  <CardBody className="p-6 space-y-6">
                    <Text variant="heading-sm" className="flex-1">
                      Pilih indikator berdasarkan:
                    </Text>
                    <div className="space-y-4 text-left">
                      {PENGEMBANGAN_KOMPETENSI_HINTS.map((hint, index) => (
                        <div key={index} className="flex space-x-3">
                          <Icon>check_circle_outlined</Icon>
                          <div className="space-y-1">
                            <Text>{hint}</Text>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button
                      color="white"
                      onClick={() => setInfoDialogOpen(true)}
                      size="md"
                      variant="ghost"
                      fullWidth
                      className="!text-blue-70 space-x-1 !outline-none !px-1"
                    >
                      <Icon color="informational" fontSize="sm">
                        help
                      </Icon>
                      <span>Tentang Pengembangan Kompetensi</span>
                    </Button>
                  </CardBody>
                </Card>
              </div>

              <div className="col-span-7">
                <div className="p-6 text-center bg-expressive-dark-blue rounded-t-md">
                  <Text variant="heading-md" color="inverse">
                    Pilih 1 Indikator Kompetensi untuk menjadi fokus peningkatan
                    kinerja Anda
                  </Text>
                </div>
                <div className="p-6 space-y-8 bg-white rounded-md gap-x-4">
                  {/* Indicators Section */}
                  <IndicatorsForm />

                  {/* Activities Section */}
                  <ActivitiesForm />
                </div>
              </div>
            </div>

            <DialogPengembanganKompetensiConfirmation
              isOpen={isConfirmationDialogOpen}
              onConfirm={submit}
              onClose={() => {
                setConfirmationDialogOpen(false)
              }}
            />

            <DialogExitPrevention
              isOpen={isExitPreventionDialogOpen}
              onConfirm={handleSaveDraft}
              onClose={() => {
                setExitPreventionDialogOpen(false)
              }}
            />

            <DialogPengembanganKompetensiInfo
              isOpen={isInfoDialogOpen}
              onClose={() => {
                setInfoDialogOpen(false)
              }}
            />

            {isPengembanganKompetensiEditable && (
              <FooterAction
                isNextDisabled={isNextDisabled}
                nextLabel="Simpan"
                onNext={() => {
                  setConfirmationDialogOpen(true)
                }}
              />
            )}
          </FormProvider>

          <ScrollDown />
        </>
      )}
    </div>
  )
}
export default PengembanganKompetensi
